import React, { useLayoutEffect, useRef } from 'react';
import { IconExternalLink } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/AuthContext';
import {
  getActiveNavigationArea,
  getActiveNavigationState,
  getPermittedNavigationAreas
} from '../constants';
import NavigationBadge from './NavigationBadge';

const ContextNavigation = () => {
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const areas = getPermittedNavigationAreas(hasPermission, user);
  const area = getActiveNavigationArea(location.pathname, areas);
  const navRef = useRef(null);
  const activeId = getActiveNavigationState(location.pathname).activeId;

  useLayoutEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector('[aria-current="page"]');
    if (!active) return;
    const revealActive = () => {
      const bounds = nav.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      // Reveal a deep-linked section without moving the page's vertical scroll.
      if (item.left < bounds.left) nav.scrollLeft += item.left - bounds.left;
      else if (item.right > bounds.right) nav.scrollLeft += item.right - bounds.right;
    };
    revealActive();
    // The sidebar and area label reflow after the window resize event; observe
    // the resulting navigation width as well so the active link stays visible.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(revealActive);
    observer?.observe(nav);
    window.addEventListener('resize', revealActive);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', revealActive);
    };
  }, [activeId, area?.id]);

  if (!area || area.items.length < 2) return null;

  return (
    <div className="sticky top-0 z-20 border-b border-glass-border bg-surface-0/95 backdrop-blur-md">
      <div className="flex min-h-[52px] items-center gap-3 overflow-hidden px-4 sm:px-6 lg:px-8">
        <span className="hidden flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary sm:block">
          {area.label}
        </span>
        <nav
          ref={navRef}
          className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto py-2 scrollbar-glass"
          aria-label={`${area.label} sections`}
        >
          {area.items.map(item => {
            const isActive = item.id === activeId;
            const className = `inline-flex min-h-11 flex-shrink-0 items-center gap-1.5 px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent ${
              isActive
                ? 'bg-accent-muted text-accent'
                : 'text-text-tertiary hover:bg-surface-2 hover:text-text-primary'
            }`;

            if (item.external) {
              return (
                <a
                  key={item.id}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                >
                  {item.label}
                  <IconExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              );
            }

            return (
              <Link
                key={item.id}
                to={item.path}
                className={className}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.label}
                {item.badge ? <NavigationBadge badge={item.badge} /> : null}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default React.memo(ContextNavigation);
